import { useToast } from "@chakra-ui/react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useCallback, useMemo, useRef, useState } from "react"
import { TransferMMWDraftsService } from "../../../../api/transferMmwDrafts"
import { TransferService, WareHouseService } from "../../../../client"
import { useWarehouseInventoryBlocks } from "../../../../hooks/useWarehouseInventoryBlocks"

export function useCreateMMW() {
  const toast = useToast()
  const navigate = useNavigate()
  const creationInFlight = useRef(false)
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false)

  const warehousesQuery = useQuery({
    queryKey: ["wms", "warehouses", "list"],
    queryFn: () => WareHouseService.getAllWarehouses(),
  })
  const warehouses = warehousesQuery.data ?? []
  const warehousesPending = warehousesQuery.isPending
  const blocksQuery = useWarehouseInventoryBlocks()
  const { blockedWarehouseIds } = blocksQuery

  const availableWarehouses = useMemo(() => {
    return warehouses.filter(
      (warehouse) =>
        warehouse.is_active &&
        !blockedWarehouseIds.has(warehouse.id) &&
        String(warehouse.code ?? "").toUpperCase() !== "BRAK",
    )
  }, [blockedWarehouseIds, warehouses])

  const createDraftMutation = useMutation({
    mutationKey: ["wms", "transfers", "mmw", "draft-init"],
    mutationFn: async (payload: {
      from_warehouse_id: string
      to_warehouse_id: string
      items?: any[]
    }) => {
      const draft = await TransferMMWDraftsService.initDocumentMMWDraft({
        from_warehouse_id: payload.from_warehouse_id,
        to_warehouse_id: payload.to_warehouse_id,
      })
      if (payload.items && payload.items.length > 0) {
        await TransferService.updateDocumentMMWByID(draft.id, {
          items: payload.items.map(
            (item) =>
              ({
                product_id: item.product_id,
                qty: item.qty,
                warehouse_lot_id: item.warehouse_lot_id,
                location_code: item.location_code,
                partia: item.partia,
                lot_no: item.lot_no,
                production_date: item.production_date,
                expiration_date: item.expiration_date,
              }) as any,
          ),
        })
      }
      return draft
    },
    onSuccess: (draft) => {
      navigate({
        to: "/WMS/documents/doc-mmw/$docMmw",
        params: { docMmw: draft.id },
        search: { tab: "general", edit: 1 },
      })
    },
    onError: (error: any) => {
      toast({
        title: "Nie udało się utworzyć szkicu MMW",
        description: error?.body?.error ?? error?.message ?? "Spróbuj ponownie",
        status: "error",
      })
    },
  })

  const createMMW = useCallback(
    async (preferredFromWarehouseId?: string, items?: any[]) => {
      if (warehousesPending || blocksQuery.isPending) {
        toast({
          status: "info",
          title: "Ładowanie danych magazynów",
          description: "Proszę chwilę poczekać i spróbować ponownie.",
        })
        return false
      }

      if (creationInFlight.current || createDraftMutation.isPending)
        return false
      creationInFlight.current = true
      setIsCheckingAvailability(true)
      try {
        const [refreshedBlocks, refreshedWarehouses] = await Promise.all([
          blocksQuery.refetch(),
          warehousesQuery.refetch(),
        ])
        if (
          refreshedBlocks.isError ||
          !refreshedBlocks.data ||
          refreshedWarehouses.isError ||
          !refreshedWarehouses.data
        ) {
          toast({
            status: "error",
            title: "Nie można sprawdzić blokady magazynu",
            description: "Odśwież dane magazynów i spróbuj ponownie.",
          })
          return false
        }
        const currentBlockedIds = new Set(refreshedBlocks.data.warehouse_ids)
        const freshAvailableWarehouses = refreshedWarehouses.data.filter(
          (warehouse) =>
            warehouse.is_active &&
            !currentBlockedIds.has(warehouse.id) &&
            String(warehouse.code ?? "").toUpperCase() !== "BRAK",
        )

        const blockedInitialWarehouse =
          preferredFromWarehouseId &&
          currentBlockedIds.has(preferredFromWarehouseId)

        if (blockedInitialWarehouse) {
          toast({
            status: "warning",
            title: "Inwentaryzacja",
            description:
              "Magazyn źródłowy z kontekstu jest obecnie zablokowany przez aktywną inwentaryzację.",
          })
          return false
        }

        const candidateFromWarehouseId =
          preferredFromWarehouseId &&
          freshAvailableWarehouses.some(
            (w) => w.id === preferredFromWarehouseId,
          ) &&
          !currentBlockedIds.has(preferredFromWarehouseId)
            ? preferredFromWarehouseId
            : freshAvailableWarehouses[0]?.id

        const candidateToWarehouseId = candidateFromWarehouseId
          ? freshAvailableWarehouses.find(
              (w) => w.id !== candidateFromWarehouseId,
            )?.id
          : undefined

        if (!candidateFromWarehouseId || !candidateToWarehouseId) {
          toast({
            status: "warning",
            title: "Brak aktywnych magazynów",
            description:
              "Brakuje dwóch aktywnych magazynów bez blokady inwentaryzacyjnej do utworzenia MMW.",
          })
          return false
        }

        await createDraftMutation.mutateAsync({
          from_warehouse_id: candidateFromWarehouseId,
          to_warehouse_id: candidateToWarehouseId,
          items,
        })
        return true
      } catch {
        return false
      } finally {
        creationInFlight.current = false
        setIsCheckingAvailability(false)
      }
    },
    [
      blocksQuery,
      createDraftMutation,
      toast,
      warehousesQuery,
      warehousesPending,
    ],
  )

  return {
    createMMW,
    isCreating: isCheckingAvailability || createDraftMutation.isPending,
    isResolvingWarehouses:
      warehousesPending || blocksQuery.isPending || blocksQuery.isError,
    canCreate:
      availableWarehouses.length >= 2 ||
      (availableWarehouses.length >= 1 &&
        availableWarehouses.some((w) => w.id !== availableWarehouses[0]?.id)),
  }
}
